import { inject, Injectable, model } from '@angular/core';
import { IDBPObjectStore, openDB, StoreNames } from 'idb';
import { catchError, combineLatest, distinctUntilChanged, forkJoin, from, lastValueFrom, map, mergeMap, Observable, of, share, shareReplay, switchMap, take, tap, throwError, withLatestFrom } from 'rxjs';
import { AppContext } from 'src/app/app/context';
import { ApiException, DataModelUserApiService, DataSchemaReference, DataSchemaType, OrganisationDataModelDto, SchemaTargetDto, SchemaType } from 'src/app/services/api.services';
import { AppError, distinctBy, isNullOrUndefined } from 'src/app/shared/utils';
import { logger } from '../utils/logger';
import { DataModel } from './DataModel';
import { SchemaUtils } from 'src/app/shared/utils/schema-utils';
import { DataRelationshipMapping, DataRelationshipMappings } from './DataRelationshipMappings';
import { isCanvasRendererHost } from '../../canvas-renderer/canvas-renderer-host';

const LOGGER = logger('DataModelResolver');

// if we got multiple requests for an item, if its not older than this it will be returned without validation
const NO_VALIDATION_WINDOW = 1000 * 30;

// On page reload, ignore no validation window
const WINDOW_LOAD_TIME = Date.now();

export interface GetModelOptions {
  /**
   * Organisation we are loading model for.
   * Although the API call knows organisation of the user, there's an idea some users may be able to toggle active organisations.
   * Providing this prepares us for that, but also means we can check cache without looking up anything else (i.e. is model for org X cached?).
   **/
  readonly organisationId?: number;

  /**
   * When true, cache is skipped.
   * Data Model will be reloaded from the server (skips request cache, indexedDB cache and local cache).
   **/
  readonly reload?: boolean;

  /**
   * When true, browser no validation window is skipped.
   * No validation window is a short interval in which we do NOT validate with the server whether the model is out of date.
   * This allows us to completely skip delays when reusing same model reference in short timespan (e.g. multiple refs to same model in N dashboard visuals).
   **/
  readonly skipNoValidationWindow?: boolean;

  readonly skipRelationshipMapping?: boolean;
}

@Injectable({ providedIn: 'root' })
export class DataModelResolver {
  readonly appContext = inject(AppContext);
  readonly dataModelUserApi = inject(DataModelUserApiService);
  readonly dataRelationshipMappings = inject(DataRelationshipMappings);

  getCombinedModel(schemas: ReadonlyArray<SchemaTargetDto>, options?: GetModelOptions): Observable<DataModel> {
    return from(this._getCombinedModel(schemas, options));
  }

  getModel(schema: SchemaTargetDto, options?: GetModelOptions): Observable<DataModel> {
    return from(this._getModel(schema, options));
  }

  getOrganisationModel(options?: GetModelOptions): Observable<DataModel> {
    return from(this._getOrganisationModel(options));
  }

  getUserDatasetModel(userDatasetId: number, options?: GetModelOptions): Observable<DataModel> {
    return from(this._getUserDatasetModel(userDatasetId, options));
  }


  _getCombinedModel(schemas: ReadonlyArray<SchemaTargetDto>, options?: GetModelOptions): Promise<DataModel> {
    if (!schemas)
      throw new Error(`getCombinedModel: schemas is null`);

    if (!schemas.length)
      return this._getOrganisationModel(options);

    let modelPromise = (async () => {
      let organisationSchemas = schemas.filter(SchemaUtils.isCore);
      let otherSchemas = schemas.filter(s => !SchemaUtils.isCore(s));

      let requests: Promise<DataModel>[] = [];

      if (organisationSchemas.length > 0)
        requests.push(this._getOrganisationModel({ ...options, skipRelationshipMapping: true }));

      requests.push(...otherSchemas.map(s => this._getModel(s, { ...options, skipRelationshipMapping: true })));

      if (requests.length == 1)
        return await requests[0];

      let models = await Promise.all(requests);
      if (models.length == 0)
        return new DataModel();

      let model = new DataModel(models[0]);

      if (models.length > 1)
        model.extensions.push(...models.slice(1));

      return model;
    })();

    if (!options?.skipRelationshipMapping)
      modelPromise = this._withRelationshipMapping(modelPromise);

    return modelPromise;
  }

  async _getModel(schema: SchemaTargetDto, options?: GetModelOptions): Promise<DataModel> {
    if (!schema)
      throw new Error(`getModel: schema is null`);

    if (SchemaUtils.isCore(schema))
      return this._getOrganisationModel(options);
    else if (SchemaUtils.isUserDataset(schema))
      return this._getUserDatasetModel(schema.userDatasetId, options);

    throw new Error(`getModel not implemented for ${schema.type}`, { cause: schema });
  }

  _getOrganisationModel(options?: GetModelOptions): Promise<DataModel> {
    let { organisationId, reload, skipNoValidationWindow } = this._unwrapOptions(options);

    let modelPromise = this._getOrLoad({
      key: `organisation:${organisationId}`,
      factory: timestamp => this.dataModelUserApi.getOrganisationDataModel(organisationId, timestamp),
      reload,
      skipNoValidationWindow
    });

    if (!options?.skipRelationshipMapping)
      modelPromise = this._withRelationshipMapping(modelPromise);

    return modelPromise;
  }

  _getUserDatasetModel(userDatasetId: number, options?: GetModelOptions): Promise<DataModel> {
    let { organisationId, reload, skipNoValidationWindow } = this._unwrapOptions(options);
    
    let modelPromise = this._getOrLoad({
      key: `userDataset:${userDatasetId}`,
      factory: timestamp => this.dataModelUserApi.getUserDatasetDataModel(organisationId, userDatasetId, timestamp),
      reload,
      skipNoValidationWindow
    });

    if (!options?.skipRelationshipMapping)
      modelPromise = this._withRelationshipMapping(modelPromise);

    return modelPromise;
  }

  private async _withRelationshipMapping(modelPromise: Promise<DataModel>) {
    let [baseModel, relationshipMapping] = await Promise.all([
      modelPromise,
      this.dataRelationshipMappings._get()
    ]);

    return new DataModel(baseModel, { relationshipMapping });
  }

  private _unwrapOptions(options?: GetModelOptions) {
    options ??= {};

    return {
      organisationId: ('organisationId' in options)
        ? options.organisationId
        : this.appContext.organisationId(),

      reload: !!options.reload,
      skipNoValidationWindow: isCanvasRendererHost || !!options.skipNoValidationWindow
    };
  }


  private readonly _loads = new Map<string, { value: DataModel, promise: Promise<DataModel> }>();
  private readonly _local = new Map<string, DataModel>();

  private async _getOrLoad(options: LoadModelOptions): Promise<DataModel> {
    let l = this._loads.get(options.key);
    if (l?.promise) {
      await l.promise;
      return l.value;
    }

    l = { value: null, promise: null };

    try {
      l.promise = this._getOrLoadImpl(options);
      this._loads.set(options.key, l);

      l.value = await l.promise;
      return l.value;
    }
    finally {
      l.promise = null;
    }
  }

  private async _getOrLoadImpl(options: LoadModelOptions): Promise<DataModel> {
    let { key, reload, skipNoValidationWindow } = options;
    let loadedDto: OrganisationDataModelDto = null;

    if (reload) {
      this.deleteCacheEntry(key);
    }

    if (!skipNoValidationWindow) {
      try {
        let cachedModelEntry = await cacheGet(key);
        if (cachedModelEntry) {
          // If cached entry exists (either local or indexed DB), we need to confirm it is
          // within the NO_VALIDATION_WINDOW interval or whether it needs validation from the server.
          let cachedModel = <OrganisationDataModelDto>cachedModelEntry.value;
          if (cachedModel
            && 'timestamp' in cachedModel && typeof cachedModel.timestamp === 'string'
            && 'modelDto' in cachedModel && typeof cachedModel.modelDto === 'object') {
            // The property checks are present just in case the cached JSON is malformed
            // (this generally shouldn't happen, but probs best not to end up with a stroke after some modifications in the code)
            loadedDto = cachedModel;

            if (cachedModelEntry.cachedTime && cachedModelEntry.cachedTime >= WINDOW_LOAD_TIME) {
              // If 'cachedTime' is present, it is checked for NO_VALIDATION_WINDOW
              // If it is not present, the general 'check with server' logic will happen
              let timeFromCached = Date.now() - cachedModelEntry.cachedTime;
              if (!isNaN(timeFromCached) && timeFromCached >= 0 && timeFromCached < NO_VALIDATION_WINDOW) {
                let m = this._local.get(key);
                if (m) {
                  LOGGER.debug('Model returned from local cache without request');
                  return m;
                }
              }
            }
          }
        }
      }
      catch (err) {
        LOGGER.error(err);
      }
    }

    loadedDto = await this._getOrLoadDto(options, loadedDto) ?? loadedDto;
    if (!loadedDto?.modelDto) 
      return new DataModel();
      
    let model = this._local.get(key);
    if (model) {
      LOGGER.debug(`Model returned from local cache after request`, { key, model, loadedDto });
      return model;
    }
    else {
      model = new DataModel(loadedDto.modelDto);
      this._local.set(key, model);
      LOGGER.debug(`Model added to local cache`, { key, model, loadedDto });
    }

    return model;
  }

  private async _getOrLoadDto(options: LoadModelOptions, loadedDto: OrganisationDataModelDto): Promise<OrganisationDataModelDto> {
    let cacheEntry = await this._getDtoFromCache(options, loadedDto);
    if (cacheEntry.cache) {
      try {
        let { key } = options;
        await cachePut(key, cacheEntry.model);
        LOGGER.debug(`ModelDto cached`, { key });
      }
      catch (err) {
        LOGGER.error(err);
        return null;
      }
    }

    return cacheEntry.model;
  }

  private async _getDtoFromCache({ key, factory }: LoadModelOptions, loadedDto: OrganisationDataModelDto): Promise<GetFromCache> {
    try {
      return {
        model: await lastValueFrom(factory(loadedDto?.timestamp)),
        cache: true
      };
    }
    catch (err) {
      if (err instanceof ApiException || err instanceof AppError) {
        if (err.status == 404) {
          // Model is missing! This can be returned for cached value, and must be respected (somebody deleted their UD)
          LOGGER.error(`ModelDto not found`, { key });
          return { model: { modelDto: null }, cache: false };
        }

        if (err.status == 304) {
          // Cached value is FRESH as confirmed by the backend
          LOGGER.debug(`ModelDto has not changed`, { key });
          return { model: loadedDto, cache: true };
        }
      }
      else {
        LOGGER.error(`Unexpected error thrown`, { key }, err);
        this.deleteCacheEntry(key);
        return { model: { modelDto: null }, cache: false };
      }
    }
  }

  private deleteCacheEntry(key: string) {
    try {
      this._local.delete(key);
      cacheDelete(key);
    }
    catch (err) {
      LOGGER.error(err);
    }
  }
}

interface LoadModelOptions {
  readonly key: string,
  readonly factory: (timestamp: string) => Observable<OrganisationDataModelDto>,
  readonly reload?: boolean,
  readonly skipNoValidationWindow?: boolean
}

interface GetFromCache {
  model: OrganisationDataModelDto;
  cache: boolean;
}

interface CacheEntry {
  key: string;
  value: any;
  cachedTime: number;
}

async function cacheDelete(key: string) {
  await cacheDbRun(cache => cache.delete(key));
}

async function cacheGet(key: string): Promise<CacheEntry> {
  let entry = <CacheEntry>await cacheDbRun(async cache => await cache.get(key));
  return entry;
}

async function cachePut(key: string, value: any): Promise<void> {
  await cacheDbRun(async cache => {
    await cache.put({ key, value, cachedTime: Date.now() });
    return value;
  });
}

const DB_NAME = 'Insights:DataModelResolver';
const TABLE_NAME = 'cache';

async function cacheGetDb() {
  return await openDB(DB_NAME, 1, {
    upgrade(db) {
      if (!db.objectStoreNames.contains(TABLE_NAME)) {
        db.createObjectStore(TABLE_NAME, {
          keyPath: 'key',
        });
      }
    }
  });
}

async function cacheDbRun<R = void>(callback: (cache: IDBPObjectStore<any, any[], StoreNames<any>, 'readwrite'>) => Promise<R>): Promise<R> {
  let db = await cacheGetDb();

  try {
    let tx = db.transaction([TABLE_NAME], 'readwrite');

    try {
      let cache = tx.objectStore(TABLE_NAME);
      let result = await callback(<any>cache);
      tx.commit();
      return result;
    }
    catch (err) {
      tx.abort();
      throw err;
    }
  }
  finally {
    db.close();
  }
}
